/**
 * A CAMPAIGN TAG, BY CONVENTION (Q7, analytics spec *Campaigns and creators*; C60, R48). The one part of a query that
 * may reach Google, and only into the first page view's `gw_page_location` after a grant.
 *
 * A tag passes only when all three parameters pass, and then they pass together; otherwise all three are dropped (a
 * half-tagged link would be misattributed):
 *
 *   utm_campaign  `CAMPAIGN_PATTERN`, 4 to 40 characters: `cr-` for a creator's campaign, `gw-` for Groundwork's own
 *   utm_source    one of `SOURCES`        (proposed)
 *   utm_medium    one of `MEDIA`          (proposed)
 *
 * WHY A CODE CAN NEVER BE A LABEL. A generated invite code is 43 characters of `A-Z a-z 0-9 _ -`
 * (`api/identity/invites.py#generate_code`): over the cap, and with capitals. A creator code (#418) must never begin
 * with `cr-` or `gw-` in any case, and #418's acceptance pins its creation against `CAMPAIGN_PATTERN` here. A label
 * that contains the code of the path it arrives on (`/i/:code`, `/invite/:code`), ignoring case, is dropped with its
 * two siblings. A label names the creator and the month, never their code (`landingpage/gtm/README.md`).
 *
 * THE CARRY. The checked tag is kept IN MEMORY FOR THE PAGE LIFE, never in a cookie or in storage (keeping it on the
 * device for analytics would itself need the consent), and rides on the first page view after a grant, so a visitor
 * who reads one page and agrees on the third still brings it. A later page life brings none.
 */
import { codeIn } from './scrub'

export const CAMPAIGN_PATTERN = /^(cr|gw)-[a-z0-9]+(-[a-z0-9]+){0,4}$/
export const CAMPAIGN_MIN_LENGTH = 4
export const CAMPAIGN_MAX_LENGTH = 40

export const SOURCES: readonly string[] = ['instagram', 'tiktok', 'youtube', 'facebook', 'x', 'newsletter', 'email', 'podcast', 'web']
export const MEDIA: readonly string[] = ['creator', 'social', 'email', 'video', 'referral']

export interface Campaign {
  source: string
  medium: string
  campaign: string
}

/** Exactly one value for *name*, or null for none or several. */
function only(params: URLSearchParams, name: string): string | null {
  const values = params.getAll(name)
  return values.length === 1 ? values[0] : null
}

/**
 * The tag an address carries, checked, or null. *search* is `location.search`; *pathname* is the page's path, whose
 * code (if it has one) a label must not contain.
 */
export function checkCampaign(search: string, pathname: string): Campaign | null {
  const params = new URLSearchParams(search)
  const source = only(params, 'utm_source')
  const medium = only(params, 'utm_medium')
  const campaign = only(params, 'utm_campaign')
  if (source === null || medium === null || campaign === null) return null
  if (!SOURCES.includes(source) || !MEDIA.includes(medium)) return null
  if (campaign.length < CAMPAIGN_MIN_LENGTH || campaign.length > CAMPAIGN_MAX_LENGTH) return null
  if (!CAMPAIGN_PATTERN.test(campaign)) return null
  const code = codeIn(pathname)
  if (code !== null && campaign.toLowerCase().includes(code.toLowerCase())) return null
  return { source, medium, campaign }
}

/** The query a checked tag is sent as. Every value is `[a-z0-9-]`, so nothing needs encoding. */
export function campaignQuery(tag: Campaign): string {
  return `?utm_source=${tag.source}&utm_medium=${tag.medium}&utm_campaign=${tag.campaign}`
}
